// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { parsePhoneNumberFromString } from 'libphonenumber-js';
import {
  type AccountOpeningFormData,
  EmploymentStatus,
  type LegalDeclarationsFormData,
  type ProfessionalProfileFormData,
  getInversisOptionId,
  type PersonalDataFormData,
} from '../types';

const requireInversisOptionId = (
  slug: string,
  namespace: Parameters<typeof getInversisOptionId>[1],
  fieldLabel: string,
): string => {
  if (!slug.trim()) {
    throw new Error(`Missing ${fieldLabel}`);
  }

  try {
    return getInversisOptionId(slug, namespace);
  } catch {
    throw new Error(`Invalid ${fieldLabel}: ${slug}`);
  }
};

export const buildClientDataFromPersonalData = (data: PersonalDataFormData) => {
  const documentType = requireInversisOptionId(
    data.document_type,
    'documentTypes',
    'document type',
  );

  const documentNumber = data.document_number;

  const nationality = requireInversisOptionId(
    data.nationality,
    'countries',
    'nationality',
  );

  const birthDate = new Date(data.birth_date);

  if (Number.isNaN(birthDate.getTime())) {
    throw new Error('Invalid birth date');
  }

  const birthDateIso = birthDate.toISOString();

  const documentExpirationDate = data.document_expiration_date
    ? new Date(data.document_expiration_date).toISOString()
    : '';

  const documentPermanentValidity = data.document_permanent_validity ? 'S' : 'N';

  const name = [data.first_name, data.second_name].filter(Boolean).join(' ');

  const firstSurname = data.first_surname;

  const secondSurname = data.second_surname ?? '';

  const gender = data.gender === 'male' ? 'H' : 'M';

  const birthCity = data.birth_city;

  const phone = parsePhoneNumberFromString(data.phone_number);

  if (!phone) {
    throw new Error('Invalid phone number');
  }

  const postalAddressCountry = requireInversisOptionId(
    data.postal_address.country,
    'countries',
    'postal country',
  );

  const postalAddressProvince =
    data.postal_address.country === 'spain'
      ? requireInversisOptionId(
          data.postal_address.province,
          'provinces',
          'postal province',
        )
      : '';

  const postalAddressStreetType =
    data.postal_address.country === 'spain'
      ? requireInversisOptionId(
          data.postal_address.street_type,
          'vias',
          'postal street type',
        )
      : '';

  const fiscalAddressStreetType =
    data.fiscal_address?.country === 'spain'
      ? requireInversisOptionId(
          data.fiscal_address.street_type ?? '',
          'vias',
          'fiscal street type',
        )
      : '';

  const fiscalAddressProvince =
    data.fiscal_address?.country === 'spain'
      ? requireInversisOptionId(
          data.fiscal_address.province ?? '',
          'provinces',
          'fiscal province',
        )
      : '';

  return {
    codigoTipoDocumento: documentType,
    numeroDocumento: documentNumber,
    indicadorFirmaDigital: 'N',
    codigoTipoTitular: '01',
    estadoCivil: data.marital_status === 'married' ? 'C' : 'S',
    fechaCaducidadDocumento: documentExpirationDate,
    fechaNacimiento: birthDateIso,
    indicadorCaducidadDocumento: documentPermanentValidity,
    codigoPaisNacionalidad: nationality,
    nombre: name,
    primerApellido: firstSurname,
    segundoApellido: secondSurname,
    paisNacimiento: nationality,
    sexo: gender,
    codigoTipoLegal: 'F',
    ciudadNacimiento: birthCity,
    email: data.email,
    datosDomicilioPostal: [
      {
        codigoPaisDomicilioPostal: postalAddressCountry,
        codigoPostal: data.postal_address.postal_code,
        codigoProvincia: postalAddressProvince,
        localidad: data.postal_address.city,
        nombreVia: data.postal_address.street_name,
        numeroVia: data.postal_address.street_number,
        codigoTipoVia: postalAddressStreetType,
        datosAdicionalesDireccion: data.postal_address.additional_address_info,
        numeroIdentificacionPaisFiscal: documentNumber,
      },
    ],
    datosDomicilioFiscal: data.has_different_fiscal_address
      ? [
          {
            codigoPostal: data.fiscal_address?.postal_code,
            codigoProvincia: fiscalAddressProvince,
            localidad: data.fiscal_address?.city,
            nombreVia: data.fiscal_address?.street_name,
            numeroVia: data.fiscal_address?.street_number,
            codigoTipoVia: fiscalAddressStreetType,
            datosAdicionalesDireccion: data.fiscal_address?.additional_address_info,
          },
        ]
      : [
          {
            codigoPostal: data.postal_address.postal_code,
            codigoProvincia: postalAddressProvince,
            localidad: data.postal_address.city,
            nombreVia: data.postal_address.street_name,
            numeroVia: data.postal_address.street_number,
            codigoTipoVia: postalAddressStreetType,
            datosAdicionalesDireccion: data.postal_address.additional_address_info,
          },
        ],
    datosTelefono: [
      {
        prefijoTelefonico: `+${phone.countryCallingCode}`,
        telefono: phone.nationalNumber,
      },
    ],
    datosNacionalidad: [
      {
        codigoPaisNacionalidad: nationality,
        datosIdentificador: [
          {
            codigoPais: nationality,
            numeroDocumentoPais: documentNumber,
          },
        ],
      },
    ],
  };
};

export const buildProfessionalProfileClientDataFields = (
  professionalProfile: ProfessionalProfileFormData,
) => {
  const workType = requireInversisOptionId(
    professionalProfile.employmentStatus,
    'employmentStatus',
    'employment status',
  );

  const sector =
    professionalProfile.employmentStatus === EmploymentStatus.NotWorking
      ? ''
      : requireInversisOptionId(professionalProfile.sector, 'sectors', 'sector');

  const activity =
    professionalProfile.employmentStatus === EmploymentStatus.NotWorking
      ? ''
      : requireInversisOptionId(
          professionalProfile.employmentStatus === EmploymentStatus.Employed
            ? professionalProfile.companyActivity
            : professionalProfile.mainActivity,
          'activities',
          'activity',
        );

  const companyName =
    professionalProfile.employmentStatus === EmploymentStatus.NotWorking
      ? ''
      : professionalProfile.employmentStatus === EmploymentStatus.Employed
      ? professionalProfile.companyName
      : professionalProfile.businessName;

  return {
    codigoSectorActividad: sector,
    codigoProfesion: activity,
    tipoTrabajo: workType,
    tipoTrabajoAnterior: '',
    indicadorOperaInternacional: '',
    codigoTipoDocumentoRepresentanteLegal: '',
    numeroDocumentoRepresentanteLegal: '',
    nombreComercial: companyName,
  };
};

export const buildDatosBeneficiarioFields = () => ({
  datosBeneficiario: [] as [],
});

export const buildDatosResidenciaFiscalFields = (
  legalDeclarations: LegalDeclarationsFormData,
) => {
  if (legalDeclarations.isUsTaxResidence) {
    const usDocumentNumber =
      legalDeclarations.taxResidenceDocumentNumber?.trim() ?? '';

    if (!usDocumentNumber) {
      throw new Error('Missing US tax residence document number');
    }

    return {
      datosResidenciaFiscal: [
        {
          codigoPais: requireInversisOptionId(
            'united-states',
            'countries',
            'tax residence country',
          ),
          numeroDocumentoPais: usDocumentNumber,
        },
      ],
    };
  }

  const codigoPais = requireInversisOptionId(
    legalDeclarations.taxResidenceCountry ?? '',
    'countries',
    'tax residence country',
  );

  const taxResidenceDocumentNumber =
    legalDeclarations.taxResidenceDocumentNumber?.trim() ?? '';

  if (!taxResidenceDocumentNumber) {
    throw new Error('Missing tax residence document number');
  }

  return {
    datosResidenciaFiscal: [
      {
        codigoPais,
        numeroDocumentoPais: taxResidenceDocumentNumber,
      },
    ],
  };
};

export const buildLegalDeclarationsClientDataFields = (
  legalDeclarations: LegalDeclarationsFormData,
) => {
  const fiscalCountry = requireInversisOptionId(
    legalDeclarations.isUsTaxResidence
      ? 'united-states'
      : legalDeclarations.taxResidenceCountry ?? '',
    'countries',
    'tax residence country',
  );

  return {
    paisFiscal: fiscalCountry,
    ...buildDatosBeneficiarioFields(),
    ...buildDatosResidenciaFiscalFields(legalDeclarations),
  };
};

export const buildClientDataFromAccountOpening = (
  data: Omit<AccountOpeningFormData, 'signature'>,
) => {
  return {
    ...buildClientDataFromPersonalData(data.personalData),
    ...buildProfessionalProfileClientDataFields(data.professionalProfile),
    ...buildLegalDeclarationsClientDataFields(data.legalDeclarations),
  };
};
